// Tiny typed wrapper around localStorage. Reasons it exists:
//   - Safari private mode + iOS Lockdown throw on writes; we silently fall
//     back to an in-memory map so the rest of the app keeps working.
//   - Adds optional TTL so transient state (form drafts, "you just viewed X")
//     auto-expires instead of accumulating forever.
//   - Centralised key prefix prevents clashes with vendor scripts.
//   - When accounts ship and we want cross-device sync, every reader/writer
//     goes through this one module — swap implementation, no call-site churn.

const PREFIX = 'tn:';
const memoryStore = new Map();

const isBrowser = typeof window !== 'undefined';

const canUseLocalStorage = (() => {
  if (!isBrowser) return false;
  try {
    const k = `${PREFIX}__probe`;
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch (_e) {
    return false;
  }
})();

const fullKey = (key) => `${PREFIX}${key}`;

const rawGet = (key) => {
  if (canUseLocalStorage) {
    try { return window.localStorage.getItem(fullKey(key)); } catch (_e) { /* fallthrough */ }
  }
  const v = memoryStore.get(fullKey(key));
  return v === undefined ? null : v;
};

const rawSet = (key, value) => {
  if (canUseLocalStorage) {
    try { window.localStorage.setItem(fullKey(key), value); return; } catch (_e) { /* fallthrough */ }
  }
  memoryStore.set(fullKey(key), value);
};

const rawDel = (key) => {
  if (canUseLocalStorage) {
    try { window.localStorage.removeItem(fullKey(key)); return; } catch (_e) { /* fallthrough */ }
  }
  memoryStore.delete(fullKey(key));
};

// ---- Public API -------------------------------------------------------

export const get = (key, fallback = null) => {
  const raw = rawGet(key);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.__ttl) {
      if (Date.now() > parsed.__ttl) {
        rawDel(key);
        return fallback;
      }
      return parsed.value;
    }
    return parsed;
  } catch (_e) {
    return fallback;
  }
};

export const set = (key, value, { ttlMs } = {}) => {
  const payload = ttlMs
    ? JSON.stringify({ value, __ttl: Date.now() + ttlMs })
    : JSON.stringify(value);
  rawSet(key, payload);
};

export const remove = (key) => rawDel(key);

export const list = (keyPrefix = '') => {
  const matchKey = fullKey(keyPrefix);
  const results = [];
  if (canUseLocalStorage) {
    try {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(matchKey)) results.push(k.slice(PREFIX.length));
      }
      return results;
    } catch (_e) { /* fallthrough */ }
  }
  memoryStore.forEach((_v, k) => {
    if (k.startsWith(matchKey)) results.push(k.slice(PREFIX.length));
  });
  return results;
};

// Idempotent push for queues (e.g. analytics events when offline)
export const pushQueue = (key, item, maxLength = 100) => {
  const queue = get(key, []) || [];
  queue.push(item);
  if (queue.length > maxLength) queue.shift();
  set(key, queue);
};

export const drainQueue = (key) => {
  const queue = get(key, []) || [];
  remove(key);
  return queue;
};
